/**
 * SystemHealthPanel Component
 *
 * Displays real-time system health metrics including:
 * - API status and uptime
 * - Component availability (Rust solver, Redis, Celery)
 * - Memory and CPU usage
 * - Solver performance statistics
 */

import React, { useEffect, useState, useCallback } from 'react';
import { dashboardApi, dataKernelApi, DataKernelStatus } from '../services/api';
import DataBadge from './DataBadge';
import DataProvenanceRow from './DataProvenanceRow';

interface HealthData {
  status: string;
  uptime_seconds: number;
  uptime_formatted: string;
  memory: { rss_mb: number; vms_mb: number; percent: number };
  cpu: { percent: number; num_threads: number };
  components: {
    rust_solver: { available: boolean; version: string | null };
    redis: { available: boolean };
    celery: { available: boolean };
  };
  timestamp: string;
}

interface SolverPerformance {
  total_solves: number;
  successful_solves: number;
  failed_solves: number;
  success_rate_percent: number;
  total_time_ms: number;
  avg_time_ms: number;
  last_solve: { time_ms: number; iterations: number; converged: boolean };
}

interface SystemHealthPanelProps {
  refreshInterval?: number;
  compact?: boolean;
  variant?: 'compact' | 'full';
}

// =============================================================================
// Status Indicator
// =============================================================================

interface StatusIndicatorProps {
  status: 'online' | 'offline' | 'warning';
  label: string;
  detail?: string;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  label,
  detail,
}) => {
  const colors = {
    online: 'bg-green-500',
    offline: 'bg-red-500',
    warning: 'bg-yellow-500',
  };

  return (
    <div className="flex items-center justify-between py-1.5 px-2 bg-gray-800/30 rounded">
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${colors[status]}`}>
          {status === 'online' && (
            <div
              className={`w-2 h-2 rounded-full ${colors[status]} animate-ping absolute`}
            />
          )}
        </div>
        <span className="text-xs text-gray-300">{label}</span>
      </div>
      {detail && <span className="text-xs text-gray-500">{detail}</span>}
    </div>
  );
};

// =============================================================================
// Metric Gauge
// =============================================================================

interface GaugeProps {
  value: number;
  max: number;
  label: string;
  unit: string;
  warningThreshold?: number;
  criticalThreshold?: number;
  size?: number;
}

const Gauge: React.FC<GaugeProps> = ({
  value,
  max,
  label,
  unit,
  warningThreshold = 70,
  criticalThreshold = 90,
  size = 80,
}) => {
  const percentage = (value / max) * 100;
  const getColor = () => {
    if (percentage >= criticalThreshold) return '#ef4444';
    if (percentage >= warningThreshold) return '#eab308';
    return '#22c55e';
  };

  // Semi-circular gauge
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * radius; // Half circle

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 10} className="overflow-visible">
        {/* Background arc */}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${
            size - strokeWidth / 2
          } ${size / 2}`}
          fill="none"
          stroke="#374151"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${
            size - strokeWidth / 2
          } ${size / 2}`}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${(percentage / 100) * circumference} ${circumference}`}
          className="transition-all duration-500"
        />
        {/* Value text */}
        <text
          x={size / 2}
          y={size / 2 - 5}
          textAnchor="middle"
          fill="#fff"
          fontSize={14}
          fontWeight="bold"
        >
          {value.toFixed(1)}
        </text>
        <text
          x={size / 2}
          y={size / 2 + 8}
          textAnchor="middle"
          fill="#9ca3af"
          fontSize={10}
        >
          {unit}
        </text>
      </svg>
      <span className="text-xs text-gray-400 mt-1">{label}</span>
    </div>
  );
};

// =============================================================================
// Performance Stats
// =============================================================================

interface PerformanceStatsProps {
  data: SolverPerformance;
}

const PerformanceStats: React.FC<PerformanceStatsProps> = ({ data }) => {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center p-2 bg-gray-800/30 rounded">
          <div className="text-lg font-bold text-white">{data.total_solves}</div>
          <div className="text-[10px] text-gray-400">Total</div>
        </div>
        <div className="text-center p-2 bg-gray-800/30 rounded">
          <div className="text-lg font-bold text-green-400">
            {data.successful_solves}
          </div>
          <div className="text-[10px] text-gray-400">Success</div>
        </div>
        <div className="text-center p-2 bg-gray-800/30 rounded">
          <div className="text-lg font-bold text-red-400">{data.failed_solves}</div>
          <div className="text-[10px] text-gray-400">Failed</div>
        </div>
      </div>

      {/* Last solve details */}
      <div className="p-2 bg-gray-800/30 rounded">
        <div className="text-xs text-gray-400 mb-1">Last Solve</div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-300">
            {data.last_solve.time_ms.toFixed(2)} ms
          </span>
          <span className="text-gray-300">{data.last_solve.iterations} iter</span>
          <span
            className={data.last_solve.converged ? 'text-green-400' : 'text-red-400'}
          >
            {data.last_solve.converged ? 'Converged' : 'Failed'}
          </span>
        </div>
      </div>

      {/* Average performance */}
      <div className="flex justify-between text-xs p-2 bg-gray-800/30 rounded">
        <span className="text-gray-400">Avg. Solve Time</span>
        <span className="text-white">{data.avg_time_ms.toFixed(2)} ms</span>
      </div>

      {/* Success rate bar */}
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-400">Success Rate</span>
          <span
            className={
              data.success_rate_percent >= 95
                ? 'text-green-400'
                : data.success_rate_percent >= 80
                ? 'text-yellow-400'
                : 'text-red-400'
            }
          >
            {data.success_rate_percent.toFixed(1)}%
          </span>
        </div>
        <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              data.success_rate_percent >= 95
                ? 'bg-green-500'
                : data.success_rate_percent >= 80
                ? 'bg-yellow-500'
                : 'bg-red-500'
            }`}
            style={{ width: `${data.success_rate_percent}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Main Panel
// =============================================================================

const SystemHealthPanel: React.FC<SystemHealthPanelProps> = ({
  refreshInterval = 5000,
  compact = false,
  variant = 'compact',
}) => {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [solver, setSolver] = useState<SolverPerformance | null>(null);
  const [dataKernel, setDataKernel] = useState<DataKernelStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isFull = variant === 'full';
  const gaugeSize = isFull ? 110 : 80;

  const fetchData = useCallback(async () => {
    try {
      const [healthData, solverData, dataKernelStatus] = await Promise.all([
        dashboardApi.getHealth(),
        dashboardApi.getSolverPerformance(),
        dataKernelApi.getStatus().catch(() => null),
      ]);
      setHealth(healthData);
      setSolver(solverData);
      setDataKernel(dataKernelStatus);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  if (loading && !health) {
    return (
      <div className="p-4 text-center text-gray-400">
        <div className="animate-pulse">Loading system health...</div>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="p-4 text-center text-red-400">
        <div>Error: {error}</div>
        <button
          onClick={fetchData}
          className="mt-2 px-3 py-1 bg-gray-700 rounded text-sm hover:bg-gray-600"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!health) return null;

  const kernelStatus: StatusIndicatorProps['status'] = (() => {
    if (!dataKernel) return 'warning';
    if (!dataKernel.kafka_enabled) return 'warning';
    return dataKernel.kafka_available ? 'online' : 'warning';
  })();

  const kernelDetail = (() => {
    if (!dataKernel) return 'unavailable';
    if (!dataKernel.kafka_enabled) return 'disabled';
    if (!dataKernel.kafka_available) return 'broker down';
    const brokerCount = dataKernel.brokers?.length ?? 0;
    return `${brokerCount} broker${brokerCount === 1 ? '' : 's'}`;
  })();

  return (
    <div
      className={`bg-gray-800/50 rounded-lg space-y-4 ${
        isFull ? 'p-4' : 'p-3'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-300">System Health</h3>
          <DataBadge tier="SYSTEM" />
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              health.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'
            }`}
          />
          <span className="text-xs text-gray-400 capitalize">{health.status}</span>
        </div>
      </div>
      <DataProvenanceRow
        tier="SYSTEM"
        source="/api/v1/dashboard/health"
        asOf={health.timestamp}
        refresh={`poll ${Math.round(refreshInterval / 1000)}s`}
        inputs={{}}
        compact
      />

      {/* Uptime */}
      <div className="text-center p-2 bg-gray-800/30 rounded">
        <div className="text-xs text-gray-400">Uptime</div>
        <div className="text-lg font-mono text-white">
          {health.uptime_formatted}
        </div>
      </div>

      {/* Resource Gauges */}
      {!compact && (
        <div className="flex justify-around">
          <Gauge
            value={health.cpu.percent}
            max={100}
            label="CPU"
            unit="%"
            warningThreshold={70}
            criticalThreshold={90}
            size={gaugeSize}
          />
          <Gauge
            value={health.memory.percent}
            max={100}
            label="Memory"
            unit="%"
            warningThreshold={70}
            criticalThreshold={90}
            size={gaugeSize}
          />
        </div>
      )}

      {/* Memory Details */}
      <div className="text-xs space-y-1">
        <div className="flex justify-between p-1.5 bg-gray-800/30 rounded">
          <span className="text-gray-400">RSS Memory</span>
          <span className="text-white">{health.memory.rss_mb.toFixed(1)} MB</span>
        </div>
        <div className="flex justify-between p-1.5 bg-gray-800/30 rounded">
          <span className="text-gray-400">Threads</span>
          <span className="text-white">{health.cpu.num_threads}</span>
        </div>
      </div>

      {/* Component Status */}
      <div className="space-y-1">
        <div className="text-xs text-gray-400 mb-2">Components</div>
        <StatusIndicator
          status={health.components.rust_solver.available ? 'online' : 'offline'}
          label="Rust Solver"
          detail={health.components.rust_solver.version || undefined}
        />
        <StatusIndicator
          status={health.components.redis.available ? 'online' : 'offline'}
          label="Redis"
        />
        <StatusIndicator
          status={health.components.celery.available ? 'online' : 'offline'}
          label="Celery Workers"
        />
        <StatusIndicator status={kernelStatus} label="Data Kernel" detail={kernelDetail} />
      </div>

      {/* Solver Performance */}
      {solver && !compact && (
        <div>
          <div className="text-xs text-gray-400 mb-2">Solver Performance</div>
          <PerformanceStats data={solver} />
        </div>
      )}

      {/* Last Update */}
      <div className="text-center text-[10px] text-gray-500">
        Last updated: {new Date(health.timestamp).toLocaleTimeString()}
      </div>
    </div>
  );
};

export default SystemHealthPanel;
